// src/oura/ui/useVisualViewport.ts
//
// A phone keyboard covers the bottom of a full-height sheet without changing
// its height (`100dvh` does not move for the keyboard either), so the foot's
// Send button sits under the keys while someone types. This hook follows the
// visual viewport instead, only while a text field inside the sheet has
// focus and the keyboard is up.
//
// API
//   useVisualViewport(ref, active) -> { top, height } | undefined
//       Spread it into the sheet's style. Undefined (leave `h-full`) unless:
//       active, under 640px wide, a text field inside `ref` has focus, and
//       visualViewport.height < innerHeight - 100.
//
// Opt in per drawer (Drawer `followKeyboard`), and only once it has passed
// the iPhone Safari and Android Chrome test in the specs (A12).

import { useEffect, useState, type CSSProperties, type RefObject } from "react";

const PHONE = "(max-width: 639px)";
const TEXT_FIELD =
    'textarea, [contenteditable]:not([contenteditable="false"]), input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"]):not([type="range"]):not([type="color"])';

interface Box {
    top: number;
    height: number;
}

export function useVisualViewport(ref: RefObject<HTMLElement | null>, active: boolean): CSSProperties | undefined {
    const [box, setBox] = useState<Box | null>(null);

    useEffect(() => {
        const vv = typeof window === "undefined" ? undefined : window.visualViewport;
        const sheet = ref.current;
        if (!active || !vv || !sheet) return;

        let frame = 0;
        const measure = () => {
            cancelAnimationFrame(frame);
            // A frame later: focusout fires before the next field's focusin,
            // and the viewport settles after the keyboard's own animation.
            frame = requestAnimationFrame(() => {
                const el = document.activeElement;
                const typing = el instanceof HTMLElement && sheet.contains(el) && el.matches(TEXT_FIELD);
                let phone = false;
                try {
                    phone = window.matchMedia(PHONE).matches;
                } catch {
                    phone = false;
                }
                if (phone && typing && vv.height < window.innerHeight - 100) {
                    const next = { top: Math.round(vv.offsetTop), height: Math.round(vv.height) };
                    setBox((b) => (b && b.top === next.top && b.height === next.height ? b : next));
                } else {
                    setBox((b) => (b ? null : b));
                }
            });
        };

        vv.addEventListener("resize", measure);
        vv.addEventListener("scroll", measure);
        sheet.addEventListener("focusin", measure);
        sheet.addEventListener("focusout", measure);
        measure();
        return () => {
            cancelAnimationFrame(frame);
            vv.removeEventListener("resize", measure);
            vv.removeEventListener("scroll", measure);
            sheet.removeEventListener("focusin", measure);
            sheet.removeEventListener("focusout", measure);
            setBox(null);
        };
    }, [active, ref]);

    return box ? { top: box.top, height: box.height } : undefined;
}
